/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState, useRef } from "react";
import * as Yup from "yup";
import AppContext from "AppContext";
import { useDispatch, useSelector } from "react-redux";
import Page from "components/common_components/Page/Page";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import { Button as ButtonMui, Grid, LinearProgress } from "@material-ui/core";
import AssignmentIcon from "@material-ui/icons/ArrowForward";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { useHistory } from "react-router-dom";
import { hasElements } from "utils/arrays";
import Icon from "components/common_components/Icon";
import LoadingPage from "components/common_components/LoadingPage";
import Button from "components/common_components/Button";
import { DatePickerField, Form, SelectField } from "components/common_components/Formik";
import Status from "components/common_components/Status";
import { ConfirmDialog, FormDialog } from "components/common_components/Dialogs";
import UserSelect from "components/common_components/UserSelect/UserSelect";
import { hasPermission } from "utils/session";
import FileCaseAssignments from "./Reports/FileCaseAssignments";
import Header from "components/common_components/Header";

function MyAssignments(props) {
    const [myAssignments, setMyAssignments] = useState({
        fileCases: [],
    });
    const dispatch = useDispatch();
    const history = useHistory();
    const resetFiltersBtnRef = useRef(null);
    const { user } = useSelector((state) => state.Login);
    const isAssignmentsAdmin = hasPermission(user, "home.my-assignments.admin");
    const canReassign = isAssignmentsAdmin || hasPermission(user, "home.my-assignments.reassign");
    const canReassignWithDueDate = isAssignmentsAdmin || hasPermission(user, "home.my-assignments.reassign.new-due-date");
    const canViewAssignmentsReport = isAssignmentsAdmin || hasPermission(user, "home.my-assignments.reports.assignments.view");
    const [isLoading, setIsLoading] = useState(false);
    const [resources, setResources] = useState({ modules: [], types: [], users: [] });
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [reportEnabled, setReportEnabled] = useState(false);
    const [changeAssigneeLoading, setChangeAssigneeLoading] = useState({});
    const [formDialogProps, setFormDialogProps] = useState({
        open: false,
        maxWidth: "sm",
        fullWidth: true,
        title: "",
        formId: "in-dialog-form",
        isSubmitting: false,
        primaryButtonText: "Assign",
        onClose: () => setFormDialogProps((prev) => ({ ...prev, open: false, isSubmitting: false })),
    });
    const [confirmDialogProps, setConfirmDialogProps] = useState({
        open: false,
        title: "",
        content: "",
        primaryButtonText: "",
        onPrimaryClick: undefined,
        secondaryButtonText: "Cancel",
        onSecondaryClick: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
        onClose: () => setConfirmDialogProps((prev) => ({ ...prev, open: false })),
    });

    const {
        api: {
            General: { MyAssignments },
            Settings: { Modules: SModules, Types, Users },
        },
    } = useContext(AppContext);

    function getModules() {
        setIsLoadingFilters(true);
        SModules.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    modules: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    modules: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function getTypes() {
        setIsLoadingFilters(true);
        Types.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    types: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    types: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    function getUsers() {
        setIsLoadingFilters(true);
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    users: data,
                }));
                setIsLoadingFilters(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    users: [],
                }));
                dispatch(notifyError(error));
                setIsLoadingFilters(false);
            },
        });
    }

    async function getMyAssignments(filters = {}) {
        try {
            setIsLoading(true);
            const res = await MyAssignments.getAllAsync({ data: filters });
            setMyAssignments(res);
        } catch (error) {
            dispatch(notifyError("Unable to fetch assignments."));
            console.error("Unable to fetch assignments!", error);
            setMyAssignments({ fileCases: [] });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getModules();
        getTypes();
        getUsers();
        getMyAssignments({ assigneeUserId: user.id });

        return () => {
            setMyAssignments({ fileCases: [] });
        };
    }, []);

    async function changeFileCaseAssignee(itemPk, newAssigneeUserId, newDueDate) {
        try {
            setChangeAssigneeLoading((prev) => ({ ...prev, [JSON.stringify(itemPk)]: true }));
            const shouldSetNewDueDate = canReassignWithDueDate;
            await MyAssignments.changeFileCaseAssignee(itemPk.module, itemPk.id, newAssigneeUserId, newDueDate, shouldSetNewDueDate);
            dispatch(notifySuccess("Assignee changed."));
        } catch (error) {
            dispatch(notifyError(error.response?.data?.message || error.message || "Unexpected error."));
        } finally {
            setChangeAssigneeLoading((prev) => ({ ...prev, [JSON.stringify(itemPk)]: false }));
        }
    }

    const onAssignToClickHandler = (event, item) => {
        event?.preventDefault();
        event?.stopPropagation();
        setFormDialogProps((prevF) => ({
            ...prevF,
            open: true,
            title: `Assign ${item.fileNo} to`,
            onSubmit: async (values, { resetForm }) => {
                const { newAssigneeUser, newDueDate } = values;
                setConfirmDialogProps((prev) => ({
                    ...prev,
                    open: true,
                    title: "Confirm Assignment",
                    content: `Are you sure you want to assign ${item.fileNo} to ${newAssigneeUser.name}?`,
                    primaryButtonText: "Yes, assign",
                    onPrimaryClick: async () => {
                        setFormDialogProps((prev) => ({ ...prev, isSubmitting: true }));
                        await changeFileCaseAssignee(item.pk, newAssigneeUser.id, newDueDate);
                        resetForm();
                        prevF.onClose();
                        prev.onClose();
                        resetFiltersBtnRef.current?.click();
                    },
                }));
            },
        }));
    };

    const headCells = [
        {
            index: 0,
            id: "module",
            label: "Module",
            render: (_, { value }) => (hasElements(resources.modules) ? resources.modules.find((x) => x.dbName === value)?.name : value),
        },
        {
            index: 1,
            id: "fileNo",
            disablePadding: false,
            label: "File No.",
            render: (_, { value, data }) => {
                return (
                    <a
                        href="#top"
                        onClick={() => {
                            const { module, id } = data;
                            if (module && id) {
                                history.push(`/${module}?fileCaseId=${id}`);
                            }
                        }}
                    >
                        {value}
                    </a>
                );
            },
        },
        { index: 2, id: "workStatus", label: "Status", render: (_, { value }) => <Status id={value} withIcon /> },
        { index: 3, id: "type.type", label: "Type" },
        { index: 4, id: "client.fullName", label: "Client" },
        { index: 5, id: "assignmentDate", label: "Assigned", type: "datetime" },
        { index: 6, id: "assigneeUser.name", label: "Assignee" },
        { index: 7, id: "dueDate", label: "Due", type: "date" },
        {
            index: 8,
            id: "actions",
            label: canReassign ? "Reassign" : "",
            render: (_, { value, data }) => {
                if (!canReassign) return null;
                const isAssignmentLoading = changeAssigneeLoading[JSON.stringify(data.pk)] || false;
                return isAssignmentLoading ? (
                    <LinearProgress />
                ) : (
                    <ButtonMui
                        variant="text"
                        size="small"
                        onClick={(e) => onAssignToClickHandler(e, data)}
                        color="default"
                        endIcon={<AssignmentIcon color="primary" />}
                        title="Assign to user"
                    >
                        Assign to ...
                    </ButtonMui>
                );
            },
        },
    ];

    const onAssignToBulkClickHandler = (selectedItems, clearSelection) => {
        if (selectedItems.length === 1) {
            onAssignToClickHandler(
                null,
                myAssignments.fileCases.find((x) => x.pk === selectedItems[0].pk)
            );
        } else {
            setFormDialogProps((prevF) => ({
                ...prevF,
                open: true,
                title: `Assign ${selectedItems.length} case(s) to`,
                onSubmit: async (values, { resetForm }) => {
                    const { newAssigneeUser, newDueDate } = values;
                    setConfirmDialogProps((prev) => ({
                        ...prev,
                        open: true,
                        title: "Confirm Assignment",
                        content: `Are you sure you want to assign ${selectedItems.length} case(s) to ${newAssigneeUser.name}?`,
                        primaryButtonText: "Yes, assign",
                        onPrimaryClick: async () => {
                            setFormDialogProps((prev) => ({ ...prev, isSubmitting: true }));
                            const promises = selectedItems.map((item) => changeFileCaseAssignee(item.pk, newAssigneeUser.id, newDueDate));
                            await Promise.all(promises);
                            resetForm();
                            prevF.onClose();
                            prev.onClose();
                            clearSelection();
                            resetFiltersBtnRef.current?.click();
                        },
                    }));
                },
            }));
        }
    };

    const renderBulkActions = (selectedItemIds, clearSelection) => {
        const selectedItems = (myAssignments?.fileCases || []).filter((x) => selectedItemIds.includes(x.pk)).map(({ pk }) => ({ pk }));
        return (
            <>
                <ButtonMui
                    variant="text"
                    size="small"
                    onClick={() => onAssignToBulkClickHandler(selectedItems, clearSelection)}
                    color="default"
                    endIcon={<AssignmentIcon color="primary" />}
                    title={`Assign ${selectedItems.length} item(s) to user`}
                >
                    {`Assign ${selectedItems.length} item(s) to ...`}
                </ButtonMui>
            </>
        );
    };

    const onSubmitHandler = (values, { setSubmitting, setFieldValue }) => {
        setSubmitting(false);
        const { module, type, assigneeUser, dueDateFrom, dueDateTo } = values;
        const filterValues = {};
        if (module) filterValues.module = resources.modules.find((x) => x.id === module).dbName;
        if (type) filterValues.typeId = type;
        if (dueDateFrom) filterValues.dueDateFrom = dueDateFrom;
        if (dueDateTo) filterValues.dueDateTo = dueDateTo;

        if (canViewAssignmentsReport) {
            filterValues.assigneeUserId = assigneeUser?.id;
        } else {
            const userToQuery = assigneeUser || user;
            filterValues.assigneeUserId = userToQuery.id;
            setFieldValue("assigneeUser", userToQuery);
        }

        getMyAssignments(filterValues);
    };

    const getUsersOptions = () => {
        return resources.users || [];
    };

    const onResetFilters = (form) => {
        form.resetForm();
        onSubmitHandler({}, form);
    };

    function onViewReportClickHandler() {
        setReportEnabled(!reportEnabled);
    }

    return (
        <Page className="my-assignments-page">
            <Header>
                {reportEnabled && (
                    <Button secondary outline onClick={onViewReportClickHandler} small>
                        <Icon name="arrow" size="xs" color="secondary" />
                        <span>Back</span>
                    </Button>
                )}
                {!reportEnabled && canViewAssignmentsReport && (
                    <Button secondary outline onClick={onViewReportClickHandler} small disabled={!hasElements(myAssignments.fileCases)}>
                        <Icon name="reports" size="xs" color="secondary" />
                        <span>View report</span>
                    </Button>
                )}
            </Header>
            {!reportEnabled && (
                <>
                    <div className="filters-container">
                        {isLoadingFilters ? (
                            <LoadingPage />
                        ) : (
                            <Form
                                validateOnChange
                                initialValues={{
                                    module: "",
                                    type: "",
                                    assigneeUser: user || null,
                                    dueDateFrom: null,
                                    dueDateTo: null,
                                }}
                                validationSchema={undefined}
                                onSubmit={onSubmitHandler}
                                showButtons={false}
                            >
                                {(form) => (
                                    <>
                                        <SelectField
                                            fieldName="module"
                                            label="Module"
                                            options={resources.modules}
                                            valueKey="id"
                                            labelKey="name"
                                        />
                                        <SelectField
                                            fieldName="type"
                                            label="Type"
                                            options={
                                                form.values.module
                                                    ? resources.types.filter((x) => x.moduleId === form.values.module)
                                                    : resources.types
                                            }
                                            valueKey="id"
                                            labelKey="type"
                                        />
                                        <DatePickerField fieldName="dueDateFrom" label="Due Date - From" />
                                        <DatePickerField fieldName="dueDateTo" label="Due Date - To" />
                                        {isAssignmentsAdmin && (
                                            <UserSelect fieldName="assigneeUser" users={getUsersOptions()} label="Assignee" />
                                        )}
                                        <div className="button-group">
                                            <Button secondary small type="submit" loading={isLoading} ref={resetFiltersBtnRef}>
                                                <Icon name="search" size="md" color="white" />
                                                <span>Search</span>
                                            </Button>
                                            <Button secondary small outline onClick={() => onResetFilters(form)}>
                                                <Icon name="cancel" size="md" color="secondary" />
                                                <span>Reset filters</span>
                                            </Button>
                                        </div>
                                    </>
                                )}
                            </Form>
                        )}
                    </div>
                    {isLoading ? (
                        <LoadingPage />
                    ) : (
                        <>
                            {hasElements(myAssignments?.fileCases) && (
                                <EnhancedTable
                                    headCells={headCells}
                                    data={myAssignments.fileCases}
                                    keyColumnName="pk"
                                    selectable={canReassign}
                                    deletable={false}
                                    filterable={false}
                                    pageable={false}
                                    orderable={false}
                                    renderBulkActions={renderBulkActions}
                                />
                            )}
                        </>
                    )}

                    <FormDialog {...formDialogProps}>
                        <Form
                            id="in-dialog-form"
                            onSubmit={formDialogProps.onSubmit}
                            validateOnChange={false}
                            initialValues={{ newAssigneeUser: null, newDueDate: null }}
                            validationSchema={Yup.object({
                                newAssigneeUser: Yup.object().nullable().required("User required!"),
                                newDueDate: Yup.date("Invalid date!").nullable().notRequired(),
                            })}
                            showButtons={false}
                        >
                            <UserSelect fieldName="newAssigneeUser" users={getUsersOptions()} label="New Assignee" />
                            {canReassignWithDueDate && <DatePickerField fieldName="newDueDate" label="New Due Date" />}
                        </Form>
                    </FormDialog>
                    <ConfirmDialog {...confirmDialogProps} />
                </>
            )}

            {reportEnabled && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FileCaseAssignments data={myAssignments.fileCases} currentUser={user} />
                    </Grid>
                </Grid>
            )}
        </Page>
    );
}

export default MyAssignments;
